import { useState } from 'react';
import { Formik, Form, Field, FieldProps, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { allAuth } from '@round/api';
import { useCountdown } from '@round/utils';
import { useWaveAuth } from '@/auth/WaveAuthContext';
import OtpCodeInput from '@/ui/LoginByCode/OtpCodeInput';

const RESEND_COUNTDOWN_SECONDS = 30;

const emailSchema = Yup.object().shape({
    email: Yup.string().trim().email('Enter a valid email').required('Email is required'),
});

const codeSchema = Yup.object().shape({
    code: Yup.string().trim().required('Enter the code we emailed you'),
});

const FieldError = ({ name }: { name: string }) => (
    <ErrorMessage name={name} component="span" className="text-xs text-wm-danger" />
);

type LoginByCodeProps = {
    /** Called once a code confirm authenticates and tokens are stored. */
    onAuthenticated: () => void | Promise<void>;
    /** Fired right after a successful code request (e.g. to also capture demand). */
    onEmailRequested?: (email: string) => void;
    initialEmail?: string;
    title?: string;
    description?: string;
};

const LoginByCode = ({
    onAuthenticated,
    onEmailRequested,
    initialEmail = '',
    title = 'Sign in to Wave',
    description = "Enter your email and, if you have an account, we'll send you a code to sign in.",
}: LoginByCodeProps) => {
    const { setTokens } = useWaveAuth();
    const [stage, setStage] = useState<'email' | 'code'>('email');
    const [email, setEmail] = useState(initialEmail);
    const [sessionToken, setSessionToken] = useState<string | null>(null);

    const { remainingTimeFormatted, isExpired, reset } = useCountdown(RESEND_COUNTDOWN_SECONDS, { autoStart: true });
    const [isResending, setIsResending] = useState(false);
    const [resendError, setResendError] = useState<string | null>(null);

    const handleResend = async () => {
        if (!sessionToken) {
            return;
        }
        setIsResending(true);
        setResendError(null);
        try {
            const response = await allAuth.resendCode(sessionToken, 'wave');
            if (response.status === 429) {
                setResendError('Please wait before requesting another code.');
                return;
            }
            if (response.status === 409) {
                setResendError('Could not resend the code. Try a different email.');
                return;
            }
            if (response.status === 200) {
                reset();
            } else {
                setResendError('Could not resend the code. Please try again.');
            }
        } catch {
            setResendError('Could not resend the code. Please try again.');
        } finally {
            setIsResending(false);
        }
    };

    // Distinct keys make React replace the whole stage rather than diff its text
    // nodes, which Google Translate swaps out and would make removeChild throw.
    if (stage === 'email') {
        return (
            <div key="email" className="flex flex-col gap-3">
                <div>
                    <h2 className="wm-section__h">{title}</h2>
                    <p className="wm-sub mb-1">{description}</p>
                </div>
                <Formik
                    key="email"
                    initialValues={{ email }}
                    validationSchema={emailSchema}
                    onSubmit={async (values, { setFieldError }) => {
                        const trimmedEmail = values.email.trim();
                        try {
                            const response = await allAuth.requestCode(trimmedEmail, 'wave');
                            if (response.status === 401) {
                                setEmail(trimmedEmail);
                                setSessionToken(response.meta.session_token ?? null);
                                onEmailRequested?.(trimmedEmail);
                                reset();
                                setResendError(null);
                                setStage('code');
                                return;
                            }
                            setFieldError('email', response.errors?.[0]?.message ?? 'Could not send a code');
                        } catch {
                            setFieldError('email', 'Could not send a code. Please try again.');
                        }
                    }}
                >
                    {(formik) => (
                        <Form className="flex flex-col gap-3">
                            <Field name="email">
                                {({ field }: FieldProps) => (
                                    <label className="wm-field">
                                        <span className="wm-field__label">Your email</span>
                                        <input
                                            className="wm-input"
                                            type="email"
                                            placeholder="you@example.com"
                                            autoComplete="email"
                                            {...field}
                                        />
                                        <FieldError name="email" />
                                    </label>
                                )}
                            </Field>
                            <button type="submit" className="wm-btn wm-btn--accent" disabled={formik.isSubmitting}>
                                {formik.isSubmitting ? 'Sending…' : 'Send me a code'}
                            </button>
                        </Form>
                    )}
                </Formik>
            </div>
        );
    }

    return (
        <div key="code" className="flex flex-col gap-3">
            <div>
                <h2 className="wm-section__h">Check your email</h2>
                <p className="wm-sub mb-1">
                    If you have an account, we&apos;ve emailed you a code. Enter it below to sign in. It looks like{' '}
                    <span className="font-medium text-wm-ink">ABCD-EFGH</span>.
                </p>
            </div>
            <Formik
                key="code"
                initialValues={{ code: '' }}
                validationSchema={codeSchema}
                onSubmit={async (values, { setFieldError }) => {
                    if (!sessionToken) {
                        setFieldError('code', 'Your session expired. Start again with your email.');
                        return;
                    }
                    try {
                        const response = await allAuth.confirmCode(values.code.trim(), sessionToken);
                        if (response.status === 200 && response.meta.is_authenticated) {
                            setTokens({ access: response.meta.access, refresh: response.meta.refresh });
                            await onAuthenticated();
                            return;
                        }
                        if (response.status === 400) {
                            setFieldError('code', response.errors?.[0]?.message ?? 'That code didn’t work.');
                            return;
                        }
                        // 401 (session invalid) / 409: neutral retry.
                        setFieldError('code', 'That didn’t work. Request a new code and try again.');
                    } catch {
                        setFieldError('code', 'Could not confirm the code. Please try again.');
                    }
                }}
            >
                {(formik) => (
                    <Form className="flex flex-col gap-3">
                        <label className="wm-field">
                            <span className="wm-field__label">Email</span>
                            <input className="wm-input" type="email" value={email} disabled readOnly />
                        </label>
                        <Field name="code">
                            {({ field, meta, form }: FieldProps) => (
                                <div className="wm-field">
                                    <span className="wm-field__label">Your code</span>
                                    <OtpCodeInput
                                        value={field.value}
                                        onChange={(value) => {
                                            form.setFieldValue('code', value);
                                            if (!meta.touched) {
                                                form.setFieldTouched('code', true, false);
                                            }
                                        }}
                                        invalid={Boolean(meta.touched && meta.error)}
                                        autoFocus
                                    />
                                    <FieldError name="code" />
                                </div>
                            )}
                        </Field>
                        <button type="submit" className="wm-btn wm-btn--accent" disabled={formik.isSubmitting}>
                            {formik.isSubmitting ? 'Signing in…' : 'Sign in'}
                        </button>
                    </Form>
                )}
            </Formik>

            <div className="flex flex-col gap-1">
                {!isExpired ? (
                    <p className="text-xs text-wm-ink-3">You can request another code in {remainingTimeFormatted}</p>
                ) : (
                    <button
                        type="button"
                        className="wm-btn wm-btn--link self-start"
                        disabled={isResending}
                        onClick={handleResend}
                    >
                        {isResending ? 'Resending…' : 'Resend code'}
                    </button>
                )}
                {resendError ? <span className="text-xs text-wm-danger">{resendError}</span> : null}
                <button
                    type="button"
                    className="wm-btn wm-btn--link self-start"
                    onClick={() => {
                        setSessionToken(null);
                        setResendError(null);
                        setStage('email');
                    }}
                >
                    Use a different email
                </button>
            </div>
        </div>
    );
};

export default LoginByCode;
